<template>
  <div class="blog-detail-container">
    <div v-if="loading" class="loading-container">
      <el-skeleton :rows="10" animated />
    </div>

    <div v-else-if="blog" class="blog-content">
      <div class="blog-header">
        <h1>{{ blog.title }}</h1>
        <div class="blog-meta">
          <div class="meta-item">
            <el-avatar :size="40" :src="blog.avatar || require('@/assets/default-avatar.jpg')" />
            <span class="author">{{ blog.authors?.join(', ') || '匿名' }}</span>
          </div>
          <div class="meta-item">
            <el-icon><Calendar /></el-icon>
            <span>{{ formatDate(blog.createdAt) }}</span>
          </div>
          <div class="meta-item" v-if="blog.twitterAuthor">
            <el-icon><Link /></el-icon>
            <span>{{ blog.twitterAuthor }}</span>
          </div>
        </div>
      </div>

      <div class="blog-banner" v-if="blog.banner">
        <el-image 
          :src="blog.banner" 
          fit="cover"
          class="banner-image"
        >
          <template #error>
            <div class="image-slot">
              <el-icon><Picture /></el-icon>
            </div>
          </template>
        </el-image>
      </div>

      <div class="blog-tags" v-if="blog.tags && blog.tags.length > 0">
        <el-tag 
          v-for="tag in blog.tags" 
          :key="tag" 
          size="default"
          class="tag"
        >
          {{ tag }}
        </el-tag>
      </div>

      <div class="blog-categories" v-if="blog.categories && blog.categories.length > 0">
        <el-tag 
          v-for="category in blog.categories" 
          :key="category" 
          type="success"
          size="default"
          class="category"
        >
          {{ category }}
        </el-tag>
      </div>

      <div class="blog-summary" v-if="blog.summary">
        <h3>摘要</h3>
        <p>{{ blog.summary }}</p>
      </div>

      <div v-if="needsPurchase" class="blog-paywall">
        <p>本文为付费资料，购买后可阅读全文。价格 ¥{{ formatFeeYuan(blog.fee) }}</p>
        <el-button type="primary" :loading="buying" @click="buyBlog">购买并阅读</el-button>
      </div>

      <div class="blog-body" v-else>
        <MarkdownBody :content="blog.content" />
      </div>

      <div class="blog-footer">
        <div class="actions">
          <el-button type="primary" @click="goBack">
            <el-icon><ArrowLeft /></el-icon>
            返回列表
          </el-button>
          <el-button v-if="isAuthor || isSuperAdmin" @click="editBlog">
            <el-icon><Edit /></el-icon>
            编辑文章
          </el-button>
        </div>
      </div>
    </div>

    <div v-else class="empty-state">
      <el-empty description="文章不存在或已被删除" />
      <el-button type="primary" @click="goBack">返回列表</el-button>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Calendar, Link, Picture, ArrowLeft, Edit } from '@element-plus/icons-vue'
import axios from 'axios'
import { ElMessage, ElMessageBox } from 'element-plus'
import { hasSysRole, isSystemAdmin } from '@/utils/sysRole'
import MarkdownBody from '@/components/MarkdownBody.vue'
import { goToLogin } from '@/utils/loginFlow'

export default {
  name: 'BlogDetailView',
  components: {
    Calendar,
    Link,
    Picture,
    ArrowLeft,
    Edit,
    MarkdownBody
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const blog = ref(null)
    const loading = ref(true)
    const buying = ref(false)
    const currentUser = ref(JSON.parse(localStorage.getItem('user_info') || '{}'))
    
    console.log(currentUser.value)

    const isAuthor = computed(() => {
      if (!blog.value || !currentUser.value) return false
      return blog.value.authors?.includes(currentUser.value.username)
    })

    const isSuperAdmin = computed(() => {
      return hasSysRole(currentUser.value?.sysRole, 'superadmin')
    })

    const isAdmin = computed(() => {
      return isSystemAdmin(currentUser.value?.sysRole)
    })

    const needsPurchase = computed(() => {
      const b = blog.value
      if (!b) return false
      if (isAuthor.value || isAdmin.value) return false
      if (Number(b.isAcquired) === 1) return false
      if (Number(b.free) === 1) return false
      return Number(b.fee) > 0
    })

    const fetchBlog = async () => {
      try {
        loading.value = true
        const token = localStorage.getItem('jwt_token')
        const response = await axios.get(`/api/v1/blog/${route.params.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.data.code === 200) {
          blog.value = response.data.data
        } else {
          ElMessage.error(response.data.message || '获取文章失败')
        }
      } catch (error) {
        console.error('Error fetching blog:', error)
        ElMessage.error('获取文章失败')
      } finally {
        loading.value = false
      }
    }

    const formatDate = (timestamp) => {
      if (!timestamp) return ''
      const date = new Date(timestamp * 1000)
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const formatFeeYuan = (fee) => {
      return ((Number(fee) || 0) / 100).toFixed(2)
    }

    const buyBlog = async () => {
      if (!blog.value) return
      const token = localStorage.getItem('jwt_token')
      if (!token) {
        goToLogin(router)
        return
      }
      try {
        await ElMessageBox.confirm(
          `确定要购买此资料吗？费用：¥${formatFeeYuan(blog.value.fee)}`,
          '购买确认',
          {
            confirmButtonText: '确定购买',
            cancelButtonText: '取消',
            type: 'info'
          }
        )
      } catch {
        return
      }
      try {
        buying.value = true
        const balanceResponse = await axios.get('/api/v1/user/balance', {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        if (balanceResponse.data.code === 200 && balanceResponse.data.data?.length > 0) {
          const userBalance = balanceResponse.data.data[0].balance
          const fee = Number(blog.value.fee) || 0
          if (userBalance < fee) {
            ElMessage.error(
              `余额不足，需要 ¥${formatFeeYuan(fee)}，当前余额 ¥${(userBalance / 100).toFixed(2)}`
            )
            router.push('/recharge')
            return
          }
        }
        const response = await axios.post(
          '/api/v1/blog/buy',
          { id: blog.value.id },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        )
        if (response.data.code === 200) {
          ElMessage.success('购买成功')
          await fetchBlog()
        } else {
          ElMessage.error(response.data.message || '购买失败')
        }
      } catch (error) {
        console.error('Error buying blog:', error)
        if (error.response?.status === 401) {
          goToLogin(router)
        } else {
          ElMessage.error(error.response?.data?.message || '购买失败，请重试')
        }
      } finally {
        buying.value = false
      }
    }

    const goBack = () => {
      router.push('/blogs')
    }

    const editBlog = () => {
      router.push(`/blogs/edit/${route.params.id}`)
    }

    const updateBlog = async (blogData) => {
      try {
        const token = localStorage.getItem('jwt_token')
        const response = await axios.post('/api/v1/blog/update', {
          id: parseInt(route.params.id),
          onlyAdmin: blog.value?.onlyAdmin ?? 0,
          ...blogData
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.data.code === 200) {
          ElMessage.success('文章更新成功')
          // 重新获取文章数据
          await fetchBlog()
        } else {
          ElMessage.error(response.data.message || '更新文章失败')
        }
      } catch (error) {
        console.error('Error updating blog:', error)
        ElMessage.error('更新文章失败')
      }
    }

    onMounted(() => {
      fetchBlog()
    })

    return {
      blog,
      loading,
      buying,
      isAuthor,
      isSuperAdmin,
      needsPurchase,
      formatDate,
      formatFeeYuan,
      buyBlog,
      goBack,
      editBlog,
      updateBlog
    }
  }
}
</script>

<style scoped>
.blog-detail-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.loading-container {
  padding: 20px;
}

.blog-content {
  background-color: #fff;
}

.blog-header {
  margin-bottom: 30px;
}

.blog-header h1 {
  margin: 0 0 20px 0;
  font-size: 32px;
  color: var(--el-text-color-primary);
  line-height: 1.4;
}

.blog-meta {
  display: flex;
  align-items: center;
  gap: 20px;
  color: var(--el-text-color-secondary);
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.blog-banner {
  margin-bottom: 20px;
}

.banner-image {
  width: 100%;
  max-height: 400px;
  object-fit: cover;
  border-radius: 8px;
}

.blog-tags,
.blog-categories {
  margin-bottom: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.blog-summary {
  margin-bottom: 30px;
  padding: 20px;
  background-color: var(--el-fill-color-light);
  border-radius: 8px;
}

.blog-summary h3 {
  margin: 0 0 10px 0;
  color: var(--el-text-color-primary);
}

.blog-summary p {
  margin: 0;
  color: var(--el-text-color-regular);
  line-height: 1.6;
}

.blog-paywall {
  margin: 24px 0;
  padding: 24px;
  border: 1px dashed var(--el-color-primary);
  border-radius: 8px;
  text-align: center;
  background: var(--el-fill-color-light);
}

.blog-paywall p {
  margin: 0 0 16px;
  color: var(--el-text-color-regular);
}

.blog-body {
  margin-bottom: 30px;
}

:deep(.markdown-body table) {
  display: block;
  width: 100%;
  overflow: auto;
  margin-top: 0;
  margin-bottom: 16px;
  border-spacing: 0;
  border-collapse: collapse;
}

:deep(.markdown-body table th) {
  background-color: var(--el-fill-color-light);
  color: var(--el-text-color-primary);
  font-weight: 600;
  padding: 6px 13px;
  border: 1px solid var(--el-border-color);
}

:deep(.markdown-body table td) {
  padding: 6px 13px;
  border: 1px solid var(--el-border-color);
}

:deep(.markdown-body table tr) {
  background-color: var(--el-bg-color);
  border-top: 1px solid var(--el-border-color);
}

:deep(.markdown-body table tr:nth-child(2n)) {
  background-color: var(--el-fill-color-lighter);
}

.blog-footer {
  border-top: 1px solid var(--el-border-color-light);
  padding-top: 20px;
  margin-top: 30px;
}

.actions {
  display: flex;
  justify-content: space-between;
}

.image-slot {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 400px;
  background: var(--el-fill-color-light);
  color: var(--el-text-color-secondary);
  font-size: 30px;
}

@media (max-width: 768px) {
  .blog-detail-container {
    padding: 10px;
  }

  .blog-content {
    padding: 10px;
  }

  .blog-header h1 {
    font-size: 24px;
  }

  .blog-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}
</style> 